import React, { useState } from "react";

import { useTranslation } from "react-i18next";

import ErrorBoundaryWithPropagation from "@components/boundary/ErrorBoundaryWithPropagation";
import TeamIcon from "@components/icons/TeamIcon";
import { Team } from "@gp/models";
import TeamLineupsStore from "@state/stores/offer/statistics/TeamLineupsStore";
import classnames from "classnames";
import { observer } from "mobx-react";

import TeamLineupsMissingPlayers from "@features/offer/shared/statistics/components/team-lineups/TeamLineupsMissingPlayers";
import TeamLineupsSubstitutions from "@features/offer/shared/statistics/components/team-lineups/TeamLineupsSubstitutions";

export default observer(function TeamLineupsBench(props: {
	store: TeamLineupsStore;
}) {
	const { t } = useTranslation();

	const teamTabs: {
		key: string;
		label: string | undefined;
		mainTeam: Team | undefined;
		opponentTeam: Team | undefined;
	}[] = [
		{
			key: "homeTeam",
			label: props.store.playerStatisticsStore.eventStore.teamOne?.name,
			mainTeam: props.store.playerStatisticsStore.eventStore.teamOne,
			opponentTeam: props.store.playerStatisticsStore.eventStore.teamTwo,
		},
		{
			key: "awayTeam",
			label: props.store.playerStatisticsStore.eventStore.teamTwo?.name,
			mainTeam: props.store.playerStatisticsStore.eventStore.teamTwo,
			opponentTeam: props.store.playerStatisticsStore.eventStore.teamOne,
		},
	];

	const homeSubstitutionsPlayers = (
		props.store.teamLineups?.homeTeamPlayers || []
	).slice(11);

	const awaySubstitutionsPlayers = (
		props.store.teamLineups?.awayTeamPlayers || []
	).slice(11);

	const [activeTab, setActiveTab] = useState<string>(teamTabs[0]?.key);

	const missingPlayers =
		activeTab === "homeTeam"
			? props.store.teamLineups?.homeMissingPlayer
			: props.store.teamLineups?.awayMissingPlayer;

	const substitutionPlayers =
		activeTab === "homeTeam"
			? homeSubstitutionsPlayers
			: awaySubstitutionsPlayers;

	const noMissingPlayers = !missingPlayers || missingPlayers.length === 0;

	return (
		<ErrorBoundaryWithPropagation componentName="Desktop/TeamLineupsDataBench.tsx">
			<div className="statistics__card">
				{teamTabs.length > 0 && (
					<div className="statistics__header u-pad--reset">
						<div className="statistics__tabs tabs--primary">
							{teamTabs.map((tab) => (
								<button
									key={tab.key}
									type="button"
									onClick={() => setActiveTab(tab.key)}
									className={classnames(
										"statistics__tabs__item tabs--primary__item tabs--primary--bordered__item",
										{
											"is-active": activeTab === tab.key,
										}
									)}
								>
									<TeamIcon
										showCountryFlag={
											props.store.playerStatisticsStore
												.eventStore.tournament?.showCountryFlag
										}
										flagCode={tab.mainTeam?.countryCode}
										jerseyTypeAbrv={tab.mainTeam?.jerseyTypeAbrv}
										opponentFlagCode={tab.opponentTeam?.countryCode}
										opponentJerseyTypeAbrv={tab.opponentTeam?.jerseyTypeAbrv}
										className="u-icon--base u-align--v--middle u-mar--right--2 u-mar--bottom--1"
									/>
									{tab.label}
								</button>
							))}
						</div>
					</div>
				)}

				<div className="statistics__body">
					{substitutionPlayers?.length > 0 &&
						(props.store.isLiveOrHistory ||
							props.store.teamLineups?.lineupConfirmed) && (
							<table className="table table--framed table--framed--light table--fixed">
								<thead className="table__head">
									<tr className="table__head__row table__row">
										<th className="table__head__cell table__cell u-type--left">
											<p
												className="u-type--clamp"
												title={t(
													"EVENT.STATISTICS.LINEUPS.SUBSTITUTIONS"
												)}
											>
												{t(
													"EVENT.STATISTICS.LINEUPS.SUBSTITUTIONS"
												)}
											</p>
										</th>
									</tr>
								</thead>

								<tbody className="table__body">
									{substitutionPlayers.map((sp, idx) => (
										<TeamLineupsSubstitutions
											substitutionPlayers={sp}
											key={idx}
											store={props.store}
										/>
									))}
								</tbody>
							</table>
						)}

					{noMissingPlayers ? (
						<table className="table table--framed table--framed--light">
							<thead className="table__head">
								<tr className="table__head__row table__row">
									<th className="table__head__cell table__cell u-type--left">
										<p
											className="u-type--clamp"
											title={t(
												"EVENT.STATISTICS.LINEUPS.INJURIES_SUSPENSIONS"
											)}
										>
											{t(
												"EVENT.STATISTICS.LINEUPS.INJURIES_SUSPENSIONS"
											)}
										</p>
									</th>
								</tr>
							</thead>

							<tbody className="table__body">
								<tr className="table__body__tr table__row">
									<td className="table__body__cell table__cell u-type--center">
										<span
											dangerouslySetInnerHTML={{
												__html: t<string>(
													"EVENT.STATISTICS.LINEUPS.NO_MISSING_PLAYERS",
													{
														team:
															activeTab ===
															"homeTeam"
																? props.store
																		.playerStatisticsStore
																		.eventStore
																		.teamOne
																		?.name
																: props.store
																		.playerStatisticsStore
																		.eventStore
																		.teamTwo
																		?.name,
														interpolation: {
															escapeValue: false,
														},
													}
												),
											}}
										/>
									</td>
								</tr>
							</tbody>
						</table>
					) : (
						missingPlayers &&
						missingPlayers.length > 0 && (
							<table className="table table--framed table--framed--light table--fixed">
								<thead className="table__head">
									<tr className="table__head__row table__row">
										<th className="table__head__cell table__cell u-type--left">
											<p
												className="u-type--clamp"
												title={t(
													"EVENT.STATISTICS.LINEUPS.INJURIES_SUSPENSIONS"
												)}
											>
												{t(
													"EVENT.STATISTICS.LINEUPS.INJURIES_SUSPENSIONS"
												)}
											</p>
										</th>
									</tr>
								</thead>

								<tbody className="table__body">
									{missingPlayers.map((mp, idx) => (
										<TeamLineupsMissingPlayers
											missingPlayers={mp}
											key={idx}
										/>
									))}
								</tbody>
							</table>
						)
					)}
				</div>
			</div>
		</ErrorBoundaryWithPropagation>
	);
});
